/**
 * LanguageSwitcher Component
 *
 * A dropdown component for switching between supported languages.
 * Persists the selection to localStorage and cookies for backend sync.
 *
 * Usage:
 *   <LanguageSwitcher variant="dropdown" />
 *   <LanguageSwitcher variant="buttons" />
 *   <LanguageSwitcher variant="minimal" />
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, type SupportedLanguage } from '../../i18n';
import { userService } from '../services';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'buttons' | 'minimal';
  className?: string;
  showFlag?: boolean;
  placement?: 'top' | 'bottom';
  align?: 'start' | 'end';
}

interface LanguageInfo {
  code: string;
  name: string;
  flag: string;
}

export function LanguageSwitcher({
  variant = 'dropdown',
  className = '',
  showFlag = true,
  placement = 'bottom',
  align = 'end',
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = (i18n.language || DEFAULT_LANGUAGE) as SupportedLanguage;
  const currentLanguage = SUPPORTED_LANGUAGES.find((l: LanguageInfo) => l.code === currentLang);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode: SupportedLanguage) => {
    if (langCode === currentLang) {
      setIsOpen(false);
      return;
    }

    // First update i18n and cookie for immediate UI feedback
    await i18n.changeLanguage(langCode);

    // Set cookie for backend sync (legacy)
    document.cookie = `lang=${langCode};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Strict`;
    // Set i18next cookie for proper language detection
    document.cookie = `i18next=${langCode};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Strict`;

    // Save language preference to backend user profile
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await userService.updateLanguage(langCode);
      }
    } catch (error) {
      // Log error but don't block the language change
      console.error('[LanguageSwitcher] Failed to save language preference:', error);
    }

    setIsOpen(false);
  };

  // Header menus open downward; footer callers can explicitly open upward.
  if (variant === 'minimal') {
    return (
      <div ref={dropdownRef} className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark hover:text-slate-900 dark:hover:text-white transition-colors"
          aria-label={t('userMenu.language', { ns: 'navigation' })}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <Globe className="h-3.5 w-3.5" />
          <span>{currentLang.toUpperCase()}</span>
        </button>

        {isOpen && (
          <div
            className={`absolute ${align === 'start' ? 'left-0' : 'right-0'} ${placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} w-52 bg-surface-card-light dark:bg-surface-card-dark rounded-lg shadow-elevated border border-surface-border-light dark:border-surface-border-dark py-1 z-50`}
            role="listbox"
          >
            {SUPPORTED_LANGUAGES.map((lang: LanguageInfo) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left transition-colors ${
                  currentLang === lang.code
                    ? 'bg-accent-soft-light text-accent-text-light dark:bg-accent-soft-dark dark:text-accent-text-dark'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-surface-2-light dark:hover:bg-surface-2-dark'
                }`}
                role="option"
                aria-selected={currentLang === lang.code}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-sm">{lang.flag}</span>
                  <span className="font-medium truncate">{lang.name}</span>
                </span>
                {currentLang === lang.code && (
                  <Check className="h-3.5 w-3.5 flex-shrink-0 text-brand-600 dark:text-brand-400" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Buttons variant - horizontal list of buttons
  if (variant === 'buttons') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {SUPPORTED_LANGUAGES.map((lang: LanguageInfo) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
              currentLang === lang.code
                ? 'bg-accent-soft-light text-accent-text-light dark:bg-accent-soft-dark dark:text-accent-text-dark'
                : 'text-text-muted-light hover:bg-surface-2-light hover:text-slate-700 dark:text-text-muted-dark dark:hover:bg-surface-2-dark dark:hover:text-slate-200'
            }`}
            aria-label={lang.name}
            title={lang.name}
          >
            {showFlag && <span className="mr-1.5">{lang.flag}</span>}
            <span>{lang.code}</span>
          </button>
        ))}
      </div>
    );
  }

  // Dropdown variant (default)
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="h-4 w-4 text-text-subtle-light dark:text-text-subtle-dark" />
        {showFlag && currentLanguage && <span className="text-base">{currentLanguage.flag}</span>}
        <span className="hidden sm:inline">{currentLanguage?.name || currentLang}</span>
        <ChevronDown
          className={`h-4 w-4 text-text-subtle-light dark:text-text-subtle-dark transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute ${align === 'start' ? 'left-0' : 'right-0'} ${placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} w-48 bg-surface-card-light dark:bg-surface-card-dark rounded-lg shadow-lg border border-surface-border-light dark:border-surface-border-dark py-1 z-50`}
          role="listbox"
        >
          {SUPPORTED_LANGUAGES.map((lang: LanguageInfo) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors ${
                currentLang === lang.code
                  ? 'bg-accent-soft-light text-accent-text-light dark:bg-accent-soft-dark dark:text-accent-text-dark'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-surface-2-light dark:hover:bg-surface-2-dark'
              }`}
              role="option"
              aria-selected={currentLang === lang.code}
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{lang.flag}</span>
                <span className="font-medium">{lang.name}</span>
              </div>
              {currentLang === lang.code && (
                <Check className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
